import type { GaugeStyle, GaugeWidget, GaugeZone, GaugeZoneTone } from '../../../widgets';
import {
  BindingRow,
  ListEditor,
  NumberField,
  OptionalBindingRow,
  Row,
  Section,
  SelectField,
  TextField,
  patcher,
  rangeError,
  type WidgetEditorProps,
} from '../fields';

const STYLES: { value: GaugeStyle; label: string }[] = [
  { value: 'radial', label: 'Rund' },
  { value: 'arc240', label: 'Bogen 240 Grad' },
  { value: 'bar', label: 'Balken' },
];

const TONES: { value: GaugeZoneTone; label: string }[] = [
  { value: 'ok', label: 'Gut' },
  { value: 'warn', label: 'Warnung' },
  { value: 'alarm', label: 'Alarm' },
];

export function GaugeEditor(
  { widget, plantId, onChange }: WidgetEditorProps<GaugeWidget>,
) {
  const patch = patcher(widget, onChange);
  const range = rangeError(widget.min, widget.max);
  const zones = widget.zones ?? [];
  return (
    <>
      <Section title="Bindung">
        <BindingRow
          label="Wert-Datenpunkt"
          value={widget.valuePoint}
          plantId={plantId}
          filter={{ valueType: 'number' }}
          onChange={(next) => patch({ valuePoint: next })}
        />
        <OptionalBindingRow
          label="Sollwert-Marke"
          value={widget.setpointPoint}
          plantId={plantId}
          filter={{ valueType: 'number' }}
          hint="Wird als Marke auf der Skala gezeichnet, nicht als zweiter Zeiger."
          onChange={(next) => patch({ setpointPoint: next })}
        />
      </Section>
      <Section title="Skala">
        <Row>
          <NumberField
            label="Unten"
            value={widget.min}
            required
            error={range}
            onChange={(next) => patch({ min: next ?? 0 })}
          />
          <NumberField
            label="Oben"
            value={widget.max}
            required
            error={range ? ' ' : undefined}
            onChange={(next) => patch({ max: next ?? 0 })}
          />
        </Row>
        <Row>
          <TextField
            label="Einheit"
            value={widget.unit}
            placeholder="Grad C"
            onChange={(next) => patch({ unit: next })}
          />
          <NumberField
            label="Nachkommastellen"
            value={widget.decimals}
            min={0}
            max={4}
            integer
            onChange={(next) => patch({ decimals: next })}
          />
        </Row>
        <SelectField
          label="Form"
          value={widget.style ?? 'radial'}
          options={STYLES}
          onChange={(next) => patch({ style: next })}
        />
      </Section>
      <Section title="Bereiche">
        <ListEditor<GaugeZone>
          label="Farbige Bereiche"
          items={zones}
          addLabel="Bereich"
          emptyHint="Ohne Bereiche bleibt die Skala einfarbig."
          hint="Die Grenzen liegen in der Einheit des Punktes. Lücken und Überlappungen sind erlaubt, der Renderer zeichnet sie in dieser Reihenfolge."
          create={() => ({ from: widget.min, to: widget.max, tone: 'ok' })}
          itemTitle={(z) => `${z.from} bis ${z.to}`}
          onChange={(next) => patch({ zones: next })}
          renderItem={(zone, patchZone) => (
            <Row>
              <NumberField
                label="Von"
                value={zone.from}
                error={rangeError(zone.from, zone.to)}
                onChange={(next) => patchZone({ from: next ?? 0 })}
              />
              <NumberField
                label="Bis"
                value={zone.to}
                onChange={(next) => patchZone({ to: next ?? 0 })}
              />
              <SelectField
                label="Ton"
                value={zone.tone}
                options={TONES}
                onChange={(next) => patchZone({ tone: next })}
              />
            </Row>
          )}
        />
      </Section>
    </>
  );
}
